import $ from "jquery";
import "./drug-categories-collapsible.scss";

/**
 * Module for transforming pregnancy/breastfeeding drug category sections into collapsible cards
 */
let transformationInProgress = false;

export const transformDrugCategoriesToCollapsible = (delay = 1500) => {
  // Prevent multiple simultaneous transformations
  if (transformationInProgress) {
    console.log("Transformation already in progress, skipping...");
    return;
  }

  transformationInProgress = true;

  // Immediately hide original content to prevent flash
  const targetContainer = findDrugCategoryContainer();
  if (targetContainer) {
    hideOriginalSections(targetContainer);
  }

  setTimeout(() => {
    if (!targetContainer) {
      transformationInProgress = false;
      return;
    }
    $("body").addClass("has-drug-categories");

    transformAllSections(targetContainer);
    setupToggleFunctionality();
    setupAZNavigation();
    hideRemainingOldHeadings();

    transformationInProgress = false;
  }, delay);
};

const findDrugCategoryContainer = () => {
  const allContainers = $(".appsectiondisplay");
  let targetContainer = null;

  allContainers.each((index, element) => {
    const $el = $(element);
    const html = $el.html();
    const hasTable = html.includes("<table");
    const hasHeading = html.includes("Drugs and their categories");

    if (hasTable || hasHeading) {
      targetContainer = $el;
      return false; // break the loop
    }
  });

  return targetContainer;
};

const hideOriginalSections = (container) => {
  // Hide all sections that will be transformed to prevent flash of original content
  const allSections = $(".appsectiondisplay");

  allSections.each((index, section) => {
    const $section = $(section);
    const html = $section.html();
    const hasTable = html.includes("<table");
    const hasHeading = html.includes("Drugs and their categories");

    if (hasTable || hasHeading) {
      // Hide the section immediately but keep it in the DOM
      $section.css("visibility", "hidden");
    }
  });
};

const transformAllSections = (container) => {
  const allSections = $(".appsectiondisplay");
  const transformedLetters = new Set();

  allSections.each((index, section) => {
    const $section = $(section);

    // Skip if this section already has a collapsible card
    if ($section.find(".drug-category-collapsible").length > 0) {
      return;
    }

    // Find heading within this specific section
    const heading = $section
      .find("*")
      .filter(function () {
        const text = $(this).text().trim();
        return /^Drugs and their categories in pregnancy and breastfeeding:\s*[A-Z]$/.test(
          text,
        );
      })
      .first();

    if (heading.length === 0) {
      return;
    }

    const match = heading
      .text()
      .trim()
      .match(/:\s*([A-Z])$/);
    const letter = match ? match[1] : "";

    if (!letter || transformedLetters.has(letter)) {
      return;
    }

    // Find the table within this same section
    const table = $section.find("table").first();
    if (table.length === 0) {
      return;
    }

    // Transform this section
    transformSection(heading, table, letter, $section);
    transformedLetters.add(letter);
  });
};

const transformSection = (heading, table, letter, section) => {
  const collapsibleCard = $(`
    <div class="drug-category-collapsible" data-letter="${letter}">
      <div class="drug-category-header" onclick="window.toggleDrugCategory('${letter}')">
        <div class="category-info">
          <span class="category-letter">${letter}</span>
          <span class="category-title">Drugs and their categories in pregnancy and breastfeeding</span>
        </div>
        <div class="expand-icon">
          <i class="tg-icons-ChevronDown"></i>
        </div>
      </div>
      <div class="drug-category-content" id="drug-content-${letter}" style="display: none;">
        <div class="drug-section">
          <div class="drug-section-content">
          </div>
        </div>
      </div>
    </div>
  `);

  // Clone with event handlers preserved using jQuery's clone(true, true)
  // First parameter: withDataAndEvents = true (clone data and event handlers)
  // Second parameter: deepWithDataAndEvents = true (clone data and events for all descendant elements)
  const clonedTable = table.clone(true, true);
  collapsibleCard.find(".drug-section-content").append(clonedTable);

  section.empty();
  section.append(collapsibleCard);

  // Add class to parent for styling
  section.addClass("drug-categories-container");

  // Restore visibility after transformation
  section.css("visibility", "visible");
};

const setupToggleFunctionality = () => {
  if (window.toggleDrugCategory) {
    return;
  }

  window.toggleDrugCategory = (letter) => {
    const content = document.getElementById(`drug-content-${letter}`);
    const expandIcon = document.querySelector(
      `[data-letter="${letter}"] .expand-icon`,
    );

    if (content && expandIcon) {
      // Check if currently open - use computed display style for more accuracy
      const computedStyle = window.getComputedStyle(content);
      const isCurrentlyOpen = computedStyle.display === "block";

      // Auto-close all other sections first
      closeAllDrugCategories();

      // Then toggle the clicked section (if it wasn't already open)
      if (!isCurrentlyOpen) {
        content.style.display = "block";
        expandIcon.classList.add("expanded");

        // Notify the sidebar that this section is now active
        updateSidebarHighlight(letter);
      } else {
        // If we're closing the section, clear sidebar highlighting
        clearSidebarHighlight();
      }
    }
  };
};

// Helper function to close all drug category sections
const closeAllDrugCategories = () => {
  const allContents = document.querySelectorAll('[id^="drug-content-"]');
  const allIcons = document.querySelectorAll(".expand-icon");

  allContents.forEach((content) => {
    content.style.display = "none";
  });

  allIcons.forEach((icon) => {
    icon.classList.remove("expanded");
  });
};

// Helper function to update sidebar highlighting when manually opening a section
const updateSidebarHighlight = (letter) => {
  try {
    // Emit the same event that A-Z navigation uses
    const event = new CustomEvent("drugCategorySelected", {
      detail: {
        letter: letter,
        isManualToggle: true, // Flag to indicate this came from manual click
        category: { letter: letter },
      },
    });
    window.dispatchEvent(event);

    // Also try to find and set the TOC item as active
    // This helps sync with the table of contents context
    const tocItems = document.querySelectorAll(
      '[role="treeitem"], .toc-item, .nav-item',
    );
    tocItems.forEach((item) => {
      const text = item.textContent || "";
      if (
        text.includes(
          `Drugs and their categories in pregnancy and breastfeeding: ${letter}`,
        )
      ) {
        // Remove active class from all other items
        tocItems.forEach((otherItem) => otherItem.classList.remove("active"));
        // Add active class to this item
        item.classList.add("active");
      }
    });
  } catch (error) {
    console.warn("Could not update sidebar highlight:", error);
  }
};

// Helper function to clear sidebar highlighting when closing all sections
const clearSidebarHighlight = () => {
  try {
    const event = new CustomEvent("drugCategorySelected", {
      detail: {
        letter: null,
        isManualToggle: true,
        category: null,
      },
    });
    window.dispatchEvent(event);

    const tocItems = document.querySelectorAll(
      '[role="treeitem"], .toc-item, .nav-item',
    );
    tocItems.forEach((item) => {
      item.classList.remove("active");
    });
  } catch (error) {
    console.warn("Could not clear sidebar highlight:", error);
  }
};

const setupAZNavigation = () => {
  window.addEventListener("drugCategorySelected", (event) => {
    const letter = event.detail.letter;
    const content = document.getElementById(`drug-content-${letter}`);
    const expandIcon = document.querySelector(
      `[data-letter="${letter}"] .expand-icon`,
    );

    if (content && expandIcon) {
      // Auto-close all sections first
      closeAllDrugCategories();

      // Then open the selected section
      content.style.display = "block";
      expandIcon.classList.add("expanded");

      setTimeout(() => {
        const header = document.querySelector(`[data-letter="${letter}"]`);
        if (header) {
          header.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  });
};

/**
 * Hide any remaining old headings that might exist outside our transformed sections
 */
const hideRemainingOldHeadings = () => {
  $("*")
    .filter(function () {
      const text = $(this).text().trim();
      return /^Drugs and their categories in pregnancy and breastfeeding:\s*[A-Z]$/.test(
        text,
      );
    })
    .each(function () {
      const $element = $(this);

      if (
        !$element.hasClass("category-title") &&
        !$element.parents(".drug-category-collapsible").length
      ) {
        $element.hide();

        const $parent = $element.parent();
        if ($parent.children().length === 1) {
          $parent.hide();
        }
      }
    });
};
